<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OptionalStepGate from './OptionalStepGate.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplateWizard from 'dashboard/routes/dashboard/whatsappTemplates/components/WhatsappTemplateWizard.vue';

const props = defineProps({
  progress: { type: Object, required: true },
  draftData: { type: Object, default: null },
});
const emit = defineEmits([
  'refresh',
  'update:can-advance',
  'update:draft-data',
]);

const { t } = useI18n();

const wizardRef = ref(null);
// A submitted draft from an earlier session in this wizard (not a backend-detected
// resource — Step08 has no real Template until Onboarding::SubmitTemplateJob runs).
const hasDraft = ref(!!props.draftData?.name);
const isEditing = ref(!hasDraft.value);

const isValid = computed(() => hasDraft.value && !isEditing.value);
watch(isValid, value => emit('update:can-advance', value), { immediate: true });

const startEditing = () => {
  isEditing.value = true;
  wizardRef.value?.reset();
};

const handleSubmit = formData => {
  hasDraft.value = true;
  isEditing.value = false;
  // The media file (if any) is intentionally dropped — Onboarding::SubmitTemplateJob
  // submits with media_file: nil, so persisting it in draft_data would be pointless
  // (it isn't JSON-serializable and can't survive a PATCH body anyway).
  emit('update:draft-data', { ...formData });
};
</script>

<template>
  <div class="flex flex-col items-center justify-start w-full p-8 md:px-16">
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_8.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/420-templates-da-meta-iniciando-conversas-fora-da-janela-de-24-horas"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_8.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_8.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.OPTIONAL_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_8.DESCRIPTION') }}</p>

      <OptionalStepGate
        :step="8"
        :progress="progress"
        :has-draft="hasDraft"
        :question="t('ONBOARDING.STEP_8.GATE_QUESTION')"
        :now-label="t('ONBOARDING.STEP_8.GATE_NOW')"
        :later-label="t('ONBOARDING.STEP_8.GATE_LATER')"
        @refresh="emit('refresh')"
        @update:can-advance="emit('update:can-advance', $event)"
      >
        <div
          v-if="hasDraft && !isEditing"
          class="flex flex-col w-full max-w-2xl gap-2"
        >
          <p class="font-medium text-n-slate-12">{{ draftData?.name }}</p>
          <p class="text-sm text-n-slate-11">{{ draftData?.body }}</p>
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('ONBOARDING.STEP_8.EDIT')"
            class="w-fit"
            @click="startEditing"
          />
        </div>

        <div v-else class="flex flex-col w-full max-w-2xl gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('ONBOARDING.STEP_8.CREATE_TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('ONBOARDING.STEP_8.CREATE_SUBTITLE') }}
            </p>
          </div>
          <WhatsappTemplateWizard
            ref="wizardRef"
            :allow-quick-reply-media="false"
            @submit="handleSubmit"
          />
        </div>
      </OptionalStepGate>
    </div>
  </div>
</template>
